import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {formValueSelector, change, Field} from 'redux-form';

import {MappingCardData} from 'shells/connected-forms/mapping-card-data';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {SpacerGroup} from 'shells/spacer/spacer-group';
import {Body} from 'shells/typography/body';

import type {FormFieldType} from 'nutshell-graphql-types';
import type {WfFormFragment_publishedVersion_fields as FormField} from 'nutshell-graphql-types';

import type {NutshellState} from '../../../store';

import type {FormFieldSchema} from '../../core/nutshell-fields/types';

import {ConnectedFieldCard} from './connected-field-card';
import {FORM_NAME} from './constants';
import {getConnectedFieldContent} from './helpers';
import {IgnoreField} from './ignore-field';
import {SelectedFieldField} from './selected-field-field';
import type {SelectedField} from './types';

import styles from './mapping-form.module.css';

const selectedFieldSelector = formValueSelector(FORM_NAME);
const isIgnoredSelector = formValueSelector(FORM_NAME);

type Props = {
    fieldType: FormFieldType;
    inputName: string;
    exampleValues: string;
    formSchema: FormFieldSchema;
    otherFields: FormField[];
};

export function MappingForm(props: Props) {
    const dispatch = useDispatch();
    const selectedField = useSelector((state: NutshellState) =>
        selectedFieldSelector(state, 'selectedField')
    );
    const isIgnored = useSelector((state: NutshellState) => isIgnoredSelector(state, 'isIgnored'));
    const onSelectedFieldChange = React.useCallback(
        (newValue: SelectedField) => {
            dispatch(change(FORM_NAME, 'selectedField', newValue));
        },
        [dispatch]
    );
    const onIsIgnoredChange = React.useCallback(
        (newValue: boolean) => {
            dispatch(change(FORM_NAME, 'isIgnored', newValue));
        },
        [dispatch]
    );

    React.useEffect(() => {
        onSelectedFieldChange(selectedField);
    }, [selectedField, onSelectedFieldChange]);

    React.useEffect(() => {
        onIsIgnoredChange(isIgnored);
    }, [isIgnored, onIsIgnoredChange]);

    return (
        <div className={styles['mapping-form']}>
            <StackLegacy spacing={8}>
                <ConnectedFieldCard
                    content={getConnectedFieldContent(props.formSchema, selectedField, isIgnored)}
                    // @ts-expect-error
                    onRemove={isIgnored ? () => onIsIgnoredChange(false) : undefined}
                />
                <SpacerGroup mb={8}>
                    <MappingCardData
                        fieldType={props.fieldType}
                        inputName={props.inputName}
                        exampleValues={props.exampleValues}
                        displayExampleValuesVertically={true}
                    />
                </SpacerGroup>
                <SpacerGroup mb={24}>
                    <Field name='isIgnored' component={IgnoreField} />
                </SpacerGroup>
                <SpacerGroup mb={8}>
                    <Body>
                        <span className={isIgnored ? styles['disabled-text'] : undefined}>
                            Select a Nutshell field to map this data to:
                        </span>
                    </Body>
                </SpacerGroup>
                <Field
                    name='selectedField'
                    component={SelectedFieldField}
                    fieldType={props.fieldType}
                    formSchema={props.formSchema}
                    isIgnored={isIgnored}
                    otherFields={props.otherFields}
                />
            </StackLegacy>
        </div>
    );
}
